// The 표 screen's store for one workbook (design 02 12.1): reads its sheets,
// cells and names, keeps them in the calc worker, applies the changes the
// socket brings, and sends my edits in order.
//
//   - Load: GET the workbook (sheets, names), the cells of each sheet by
//     pages, then the worker gets the built-in names (거래, 지출, …), the
//     sheets, the names and the cells, and calculates everything once.
//   - Changes: cells.updated -> the cells (newer ones win, model.ts);
//     sheet.structure -> the sheet is read again from the server (the
//     waiting edits move with it); sheet.updated -> the worker's sheets;
//     name.updated -> the worker's names. Changes that come while a sheet
//     is being read are applied after it.
//   - Edits: shown at once and calculated in the worker, sent in order
//     (PUT, at most 5000 cells) with the structure version they were made
//     on. 409 stale: the sheet is read again; the waiting edits have moved
//     with the sheet.structure change(s) meanwhile and go again. When the
//     change does not come (a few tries), they are dropped and the person
//     is told. Waiting edits live in memory only: closing the app before
//     they are sent loses them (the screen shows them as not sent yet).
//   - The worker is told only what changed: what it has of every cell is
//     kept (pushed), and the cells marked dirty by the model are compared.
//   - Other screens ask the same worker (evaluate): the 텐서 list for the
//     dimensions of computed tensors, saved views for their sources. The
//     version goes up after every change of the worker's data, so that they
//     ask again; asking does not change it.
import { ApiError, NetworkError, errorText } from '../../api/client'
import type { Id, TensorCell, TensorCellInput, TensorChange, TensorName, TensorSheet, TensorSheetInput, tapi } from '../api'
import type { CalcClient } from '../calc/calcClient'
import type { CalcAnswer, CalcCommand, CalcValue } from '../calc/protocol'
import { type BuiltinData, type BuiltinMore, type BuiltinRow, builtinCommands, builtinData, kstClock } from './builtins'
import { type Axis, CELLS_PER_PUT, type CellEdit, type GridOp, GridModel, type Style, cellKey, mergeStyle } from './model'

export type Api = Pick<
  typeof tapi,
  | 'workbook'
  | 'cells'
  | 'setCells'
  | 'structure'
  | 'addSheet'
  | 'updateSheet'
  | 'deleteSheet'
  | 'addName'
  | 'updateName'
  | 'deleteName'
>

/** What the stage-1 data looks like now (the rows with my waiting changes),
    and the dimensions people made. */
export interface Ledger extends BuiltinMore {
  rows: readonly BuiltinRow[]
  items: Parameters<typeof builtinData>[1]
  budgets: Parameters<typeof builtinData>[2]
}

export interface GridDeps {
  api: Api
  calc: CalcClient
  toast: (text: string) => void
  signedOut: () => void
  /** the stage-1 data, and a way to hear when it changes */
  ledger: () => Ledger | null
  onLedger: (l: () => void) => () => void
  /** the socket's changes */
  onChanges: (l: (c: TensorChange[] | 'reset') => void) => () => void
  /** the connection is back: send what waits */
  onOnline: (l: () => void) => () => void
  now?: () => number
}

export interface CellView {
  input: string
  style: Style | null
  pending: boolean
  value: CalcValue
  flags: number
}

const RETRY_MIN_MS = 2000
const RETRY_MAX_MS = 30_000
const STALE_TRIES = 6
const STALE_WAIT_MS = 500
const PAGE = 10_000
const WORKER_CHUNK = 20_000

export const STALE_DROPPED = (n: number) => `다른 사람이 행·열을 바꿔서 고친 칸 ${n}개를 보내지 못했어요. 다시 적어 주세요.`

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export class GridStore {
  readonly model: GridModel
  status: 'loading' | 'ready' | 'error' = 'loading'
  error: string | null = null
  /** values from the worker: sheet -> cell -> [value, flags] */
  values = new Map<Id, Map<number, [CalcValue, number]>>()
  version = 0

  private listeners = new Set<() => void>()
  private offs: (() => void)[] = []
  private buffered: (TensorChange[] | 'reset')[] | null = []
  private loading = new Map<Id, { again: boolean; buffer: TensorChange[] }>()
  private pushed = new Map<Id, Map<number, string>>()
  private wnames = new Map<Id, string>()
  private wdefs = new Map<string, string>()
  private flushQueued = false
  private pumping = false
  private retryTimer: ReturnType<typeof setTimeout> | undefined
  private retryDelay = RETRY_MIN_MS
  private ledgerTimer: ReturnType<typeof setTimeout> | undefined
  private clockTimer: ReturnType<typeof setInterval> | undefined
  private lastLedger: Ledger | null = null
  /** what the worker has of the built-in names (to take away what goes) */
  private lastData: BuiltinData | null = null
  private today = 0
  private disposed = false
  /** calculations in flight (tests wait for them) */
  private inflight = new Set<Promise<unknown>>()
  private readyWaiters: (() => void)[] = []

  constructor(
    readonly workbookId: Id,
    private deps: GridDeps,
  ) {
    this.model = new GridModel(workbookId)
  }

  // ---- listeners (the screen) ----

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l)
    return () => this.listeners.delete(l)
  }

  getVersion = (): number => this.version

  private changed(): void {
    this.version++
    for (const l of this.listeners) l()
  }

  // ---- start and stop ----

  async start(): Promise<void> {
    this.offs.push(this.deps.onChanges((c) => this.onChanges(c)))
    this.offs.push(this.deps.onLedger(() => this.ledgerChanged()))
    this.offs.push(this.deps.onOnline(() => this.flushOps()))
    try {
      await this.load()
    } catch (e) {
      if (this.disposed) return
      if (e instanceof ApiError && e.status === 401) this.deps.signedOut()
      this.status = 'error'
      this.error = errorText(e)
      this.changed()
    } finally {
      for (const w of this.readyWaiters.splice(0)) w()
    }
  }

  /** Resolves once loaded (or failed: status 'error'). */
  ready(): Promise<void> {
    if (this.status !== 'loading') return Promise.resolve()
    return new Promise((r) => this.readyWaiters.push(r))
  }

  /**
   * Formulas calculated in the workbook as it is now (sheets, names, the
   * built-in names, tensors), one answer each; nothing is kept. For the
   * screens other than 표 (design 02 3.4, 3.6).
   */
  async evaluate(formulas: string[]): Promise<CalcAnswer[]> {
    await this.ready()
    if (this.status !== 'ready') throw new Error(this.error ?? '계산할 수 없습니다.')
    await this.settle()
    if (formulas.length === 0) return []
    return this.deps.calc.run(formulas.map((formula) => ({ cmd: 'eval' as const, formula })))
  }

  dispose(): void {
    this.disposed = true
    for (const off of this.offs) off()
    this.offs = []
    clearTimeout(this.retryTimer)
    clearTimeout(this.ledgerTimer)
    clearInterval(this.clockTimer)
    this.deps.calc.terminate()
    this.listeners.clear()
  }

  /** Waits for the calculations sent so far (tests). */
  async settle(): Promise<void> {
    for (let i = 0; i < 20; i++) {
      await Promise.resolve()
      if (this.inflight.size === 0 && !this.flushQueued) return
      await Promise.all([...this.inflight].map((p) => p.catch(() => undefined)))
    }
  }

  private async load(): Promise<void> {
    const full = await this.deps.api.workbook(this.workbookId)
    for (const s of full.sheets) this.model.putSheet(s)
    for (const n of full.names) this.model.putName(n)
    for (const s of this.model.sheetList()) await this.readCells(s.id)
    if (this.disposed) return
    const clock = kstClock(this.now())
    this.today = clock.today
    const cmds: CalcCommand[] = [{ cmd: 'reset' }, { cmd: 'set_clock', ...clock, recalc: false }]
    const ledger = this.deps.ledger()
    this.lastData = null // the worker starts empty (reset)
    if (ledger) {
      this.lastLedger = ledger
      cmds.push(...this.builtins(ledger, clock.today))
    }
    cmds.push(...this.workerSync(false))
    cmds.push({ cmd: 'recalc', all: true })
    await this.runCalc(cmds)
    this.status = 'ready'
    // what came while loading
    const waiting = this.buffered ?? []
    this.buffered = null
    for (const c of waiting) this.onChanges(c)
    this.clockTimer = setInterval(() => this.tick(), 60_000)
    this.changed()
    this.pump()
  }

  private now(): number {
    return this.deps.now?.() ?? Date.now()
  }

  /** Reads a sheet's cells by pages into the model. */
  private async readCells(sheetId: Id): Promise<void> {
    const cells: TensorCell[] = []
    let row = -1
    let col = -1
    let sv = 0
    for (;;) {
      const page = await this.deps.api.cells(sheetId, row, col, PAGE)
      if (sv && page.structure_version !== sv) {
        // the sheet changed under us: from the start
        cells.length = 0
        row = -1
        col = -1
        sv = 0
        continue
      }
      sv = page.structure_version
      cells.push(...page.cells)
      if (!page.next) break
      row = page.next.row
      col = page.next.col
    }
    this.model.replaceCells(sheetId, sv, cells)
  }

  /** Reads a sheet again (after a structure change, or stale). */
  async reloadSheet(sheetId: Id): Promise<void> {
    const cur = this.loading.get(sheetId)
    if (cur) {
      cur.again = true
      return
    }
    const state = { again: false, buffer: [] as TensorChange[] }
    this.loading.set(sheetId, state)
    this.changed()
    try {
      do {
        state.again = false
        if (!this.model.sheets.has(sheetId)) break
        await this.readCells(sheetId)
        // what came while reading goes on top of what was read (not into
        // the buffer again: it would be lost when the loop ends)
        const buffer = state.buffer
        state.buffer = []
        for (const c of buffer) this.applyOne(c, true)
      } while (state.again && !this.disposed)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) this.model.removeSheet(sheetId)
      else if (!(e instanceof NetworkError)) this.deps.toast(errorText(e))
    } finally {
      this.loading.delete(sheetId)
      this.scheduleFlush()
      this.changed()
    }
  }

  // ---- the socket's changes ----

  private onChanges(c: TensorChange[] | 'reset'): void {
    if (this.disposed) return
    if (this.buffered) {
      this.buffered.push(c)
      return
    }
    if (c === 'reset') {
      // everything again: sheets, names, and every sheet's cells
      void this.deps.api
        .workbook(this.workbookId)
        .then((full) => {
          for (const s of full.sheets) this.model.putSheet(s)
          for (const n of full.names) this.model.putName(n)
          const known = new Set(full.sheets.map((s) => s.id))
          for (const id of [...this.model.sheets.keys()]) if (!known.has(id)) this.model.removeSheet(id)
          for (const id of [...this.model.names.keys()]) if (!full.names.some((n) => n.id === id)) this.model.names.delete(id)
          for (const s of this.model.sheetList()) void this.reloadSheet(s.id)
          this.scheduleFlush()
          this.changed()
        })
        .catch(() => undefined)
      return
    }
    let any = false
    for (const ch of c) {
      if (ch.workbook_id !== this.workbookId) continue
      any = true
      this.applyOne(ch)
    }
    if (any) {
      this.scheduleFlush()
      this.changed()
    }
  }

  /** A change, or kept for later while its sheet is being read again
      (`now`: apply it whatever, the reading is done). */
  private applyOne(ch: TensorChange, now = false): void {
    if (ch.type === 'cells.updated' && !now) {
      const sheetId = (ch.data as { sheet_id: Id }).sheet_id
      const l = this.loading.get(sheetId)
      if (l) {
        l.buffer.push(ch)
        return
      }
    }
    const fx = this.model.applyChange(ch)
    for (const id of fx.reload) void this.reloadSheet(id)
    if (fx.dropped)
      this.deps.toast('다른 사람이 같은 곳의 행·열을 먼저 바꿔서, 기다리던 행·열 바꾸기를 하지 않았어요. 다시 해 주세요.')
  }

  /** The cells a PUT answered with: like a change of them (kept until a
      reading of the sheet is done, which may have started before the PUT). */
  private answerCells(sheetId: Id, sv: number, cells: TensorCell[]): void {
    const ch = { id: 0, type: 'cells.updated', workbook_id: this.workbookId, data: { sheet_id: sheetId, structure_version: sv, cells } } as TensorChange
    this.applyOne(ch)
  }

  // ---- the worker ----

  /** Commands that bring the worker to the model: sheets, names, cells. */
  private workerSync(recalc = true): CalcCommand[] {
    const cmds: CalcCommand[] = []
    const m = this.model
    // sheets that went, were renamed, or came (in order)
    for (const [id, name] of [...this.wnames]) {
      if (!m.sheets.has(id)) {
        cmds.push({ cmd: 'remove_sheet', name, recalc: false })
        this.wnames.delete(id)
        this.pushed.delete(id)
        this.values.delete(id)
      }
    }
    for (const s of m.sheetList()) {
      const w = this.wnames.get(s.id)
      if (w === undefined) {
        cmds.push({ cmd: 'add_sheet', name: s.name, recalc: false })
        this.wnames.set(s.id, s.name)
      } else if (w !== s.name) {
        cmds.push({ cmd: 'rename_sheet', from: w, to: s.name, recalc: false })
        this.wnames.set(s.id, s.name)
      }
    }
    // names
    const want = new Map<string, [string, string]>()
    for (const n of m.names.values()) want.set(n.name.toLowerCase(), [n.name, '=' + n.formula.replace(/^=/, '')])
    const names: [string, string][] = []
    for (const [low, f] of this.wdefs) if (!want.has(low)) names.push([f.split('\u0000')[0] ?? low, ''])
    for (const [low, [name, formula]] of want) {
      const had = this.wdefs.get(low)
      if (had !== `${name}\u0000${formula}`) names.push([name, formula])
    }
    this.wdefs = new Map([...want].map(([low, [name, formula]]) => [low, `${name}\u0000${formula}`]))
    if (names.length) cmds.push({ cmd: 'set_names', names, recalc: false })
    // cells
    const cells: [string, number, number, string][] = []
    for (const [sheetId, keys] of m.takeDirty()) {
      const name = this.wnames.get(sheetId)
      if (name === undefined) continue
      let pushed = this.pushed.get(sheetId)
      if (!pushed) this.pushed.set(sheetId, (pushed = new Map()))
      for (const k of keys) {
        const input = m.shownInput(sheetId, k)
        if ((pushed.get(k) ?? '') === input) continue
        if (input === '') pushed.delete(k)
        else pushed.set(k, input)
        cells.push([name, Math.floor(k / 16384), k % 16384, input])
      }
    }
    for (let i = 0; i < cells.length; i += WORKER_CHUNK)
      cmds.push({ cmd: 'set_cells', cells: cells.slice(i, i + WORKER_CHUNK), recalc: false })
    if (recalc && cmds.length) cmds.push({ cmd: 'recalc' })
    return cmds
  }

  private scheduleFlush(): void {
    if (this.flushQueued || this.status !== 'ready') return
    this.flushQueued = true
    queueMicrotask(() => {
      this.flushQueued = false
      if (this.disposed) return
      const cmds = this.workerSync()
      if (cmds.length) void this.runCalc(cmds)
    })
  }

  /** Runs commands in the worker and takes the values that changed. */
  private runCalc(cmds: CalcCommand[]): Promise<CalcAnswer[]> {
    const ids = new Map<string, Id>()
    for (const [id, name] of this.wnames) ids.set(name, id)
    const p = this.deps.calc.run(cmds).then(
      (answers) => {
        answers.forEach((a, i) => {
          if (!a.ok) {
            console.warn('[calc]', cmds[i]?.cmd, a.error)
            return
          }
          if (a.errors?.length) console.warn('[calc]', cmds[i]?.cmd, a.errors.slice(0, 5))
          for (const [sheet, row, col, value, flags] of a.changes ?? []) {
            const id = ids.get(sheet)
            if (id === undefined) continue
            let vals = this.values.get(id)
            if (!vals) this.values.set(id, (vals = new Map()))
            const k = cellKey(row, col)
            if (value === null && flags === 0) vals.delete(k)
            else vals.set(k, [value, flags])
          }
        })
        // other screens ask again after any change of the worker's data
        if (!this.disposed) this.changed()
        return answers
      },
      (e: unknown) => {
        console.error('[calc]', e)
        if (!this.disposed && this.status === 'loading') throw e
        return [] as CalcAnswer[]
      },
    )
    this.inflight.add(p)
    void p.finally(() => this.inflight.delete(p)).catch(() => undefined)
    return p
  }

  /** The built-in names follow the rows, items and budgets (a moment after they change). */
  private ledgerChanged(): void {
    clearTimeout(this.ledgerTimer)
    this.ledgerTimer = setTimeout(() => this.refreshLedger(), 250)
  }

  refreshLedger(force = false): void {
    if (this.status !== 'ready' || this.disposed) return
    const l = this.deps.ledger()
    if (!l) return
    const last = this.lastLedger
    if (
      !force &&
      last &&
      last.rows === l.rows &&
      last.items === l.items &&
      last.budgets === l.budgets &&
      last.dims === l.dims &&
      last.tensors === l.tensors &&
      last.entries === l.entries
    )
      return
    this.lastLedger = l
    const clock = kstClock(this.now())
    this.today = clock.today
    void this.runCalc([{ cmd: 'set_clock', ...clock, recalc: false }, ...this.builtins(l, clock.today), { cmd: 'recalc' }])
  }

  /** The commands that give the worker the built-in names now. */
  private builtins(l: Ledger, today: number): CalcCommand[] {
    const data = builtinData(l.rows, l.items, l.budgets, monthOfSerial(today), l)
    const cmds = builtinCommands(data, this.lastData)
    this.lastData = data
    return cmds
  }

  /** Every minute: NOW() moves (and TODAY() on a new day, with this month in 월). */
  private tick(): void {
    const clock = kstClock(this.now())
    if (clock.today !== this.today) this.refreshLedger(true)
    else void this.runCalc([{ cmd: 'set_clock', ...clock, recalc: false }, { cmd: 'recalc' }])
  }

  // ---- what the screen reads ----

  sheets(): TensorSheet[] {
    return this.model.sheetList()
  }

  cell(sheetId: Id, row: number, col: number): CellView | undefined {
    const shown = this.model.shown(sheetId, row, col)
    const v = this.values.get(sheetId)?.get(cellKey(row, col))
    if (!shown && !v) return undefined
    return {
      input: shown?.input ?? '',
      style: shown?.style ?? null,
      pending: shown?.pending ?? false,
      value: v ? v[0] : null,
      flags: v ? v[1] : 0,
    }
  }

  /** The values of a rectangle (rows of cells), for charts and copying. */
  valuesOf(sheetId: Id, r1: number, c1: number, r2: number, c2: number): CalcValue[][] {
    const vals = this.values.get(sheetId)
    const out: CalcValue[][] = []
    for (let r = r1; r <= r2; r++) {
      const row: CalcValue[] = []
      for (let c = c1; c <= c2; c++) row.push(vals?.get(cellKey(r, c))?.[0] ?? null)
      out.push(row)
    }
    return out
  }

  /** The used rectangle of a sheet: cells with input, style or a value (spills). */
  used(sheetId: Id): { rows: number; cols: number } {
    const u = this.model.usedRange(sheetId)
    for (const k of this.values.get(sheetId)?.keys() ?? []) {
      const r = Math.floor(k / 16384)
      const c = k % 16384
      if (r > u.rows) u.rows = r
      if (c > u.cols) u.cols = c
    }
    return u
  }

  /** The sheet is being read again or rows/columns are moving: no edits now. */
  busy(sheetId: Id): boolean {
    return this.loading.has(sheetId) || this.model.ops.some((o) => o.sheetId === sheetId && o.type === 'structure')
  }

  pendingCount(): number {
    let n = 0
    for (const o of this.model.ops) n += o.type === 'cells' ? o.edits.length : 1
    return n
  }

  translateAll(items: { formula: string; drow: number; dcol: number }[]): Promise<(string | null)[]> {
    return this.deps.calc.translateAll(items)
  }

  // ---- my edits ----

  /** Typed, pasted or cleared cells: shown and calculated now, sent in order. */
  setCells(sheetId: Id, edits: CellEdit[]): void {
    if (!this.model.sheets.has(sheetId) || edits.length === 0) return
    for (let i = 0; i < edits.length; i += CELLS_PER_PUT) this.model.edit(sheetId, edits.slice(i, i + CELLS_PER_PUT))
    this.scheduleFlush()
    this.changed()
    this.pump()
  }

  /** A style change of cells (their inputs stay). */
  setStyle(sheetId: Id, cells: { row: number; col: number }[], patch: Partial<Record<keyof Style, unknown>>): void {
    const edits: CellEdit[] = []
    for (const { row, col } of cells) {
      const cur = this.model.shown(sheetId, row, col)
      const style = mergeStyle(cur?.style, patch)
      if (!cur && style === null) continue
      edits.push({ row, col, input: cur?.input ?? '', style })
    }
    this.setCells(sheetId, edits)
  }

  /** Inserts (count > 0) or deletes (count < 0) rows or columns: after the edits before it. */
  structure(sheetId: Id, axis: Axis, at: number, count: number): void {
    if (!this.model.structure(sheetId, axis, at, count)) return
    this.changed()
    this.pump()
  }

  private pump(): void {
    if (this.pumping || this.status !== 'ready') return
    this.pumping = true
    void (async () => {
      try {
        for (;;) {
          if (this.disposed) return
          const op = this.model.ops[0]
          if (!op) break
          const ok = await this.send(op)
          if (!ok) break
        }
      } finally {
        this.pumping = false
      }
    })()
  }

  /** The connection is back. */
  flushOps(): void {
    clearTimeout(this.retryTimer)
    this.model.requeue()
    this.pump()
  }

  private retryLater(): void {
    this.model.requeue()
    clearTimeout(this.retryTimer)
    this.retryTimer = setTimeout(() => this.flushOps(), this.retryDelay)
    this.retryDelay = Math.min(this.retryDelay * 2, RETRY_MAX_MS)
    this.changed()
  }

  /** False when sending must wait (no connection). */
  private async send(op: GridOp): Promise<boolean> {
    this.model.setStatus(op.id, 'sending')
    try {
      if (op.type === 'cells') {
        if (op.edits.length) {
          const body: TensorCellInput[] = op.edits.map((e) => ({
            row: e.row,
            col: e.col,
            input: e.input,
            ...(e.style !== undefined ? { style: e.style } : {}),
          }))
          const r = await this.deps.api.setCells(op.sheetId, op.sv, body)
          this.answerCells(op.sheetId, r.structure_version, r.cells)
        }
        this.model.finish(op.id)
      } else {
        const r = await this.deps.api.structure(op.sheetId, op.sv, op.axis, op.at, op.count)
        this.model.putSheet(r.sheet)
        this.model.finish(op.id)
        void this.reloadSheet(op.sheetId)
      }
      this.retryDelay = RETRY_MIN_MS
      this.scheduleFlush()
      this.changed()
      return true
    } catch (e) {
      if (e instanceof NetworkError) {
        this.retryLater()
        return false
      }
      if (e instanceof ApiError && e.status === 401) {
        this.model.requeue()
        this.deps.signedOut()
        return false
      }
      if (e instanceof ApiError && e.status === 409 && e.code === 'stale') {
        if (op.type === 'structure') {
          this.model.finish(op.id)
          this.deps.toast('다른 사람이 먼저 행·열을 바꿨어요. 다시 해 주세요.')
          await this.reloadSheet(op.sheetId)
        } else {
          await this.stale(op.sheetId)
        }
        this.changed()
        return true
      }
      if (e instanceof ApiError && e.status === 404) {
        this.model.dropOps(op.sheetId)
        this.model.removeSheet(op.sheetId)
        this.deps.toast('지워진 시트라 보내지 못했어요.')
      } else {
        this.model.finish(op.id)
        this.deps.toast(errorText(e))
      }
      this.scheduleFlush()
      this.changed()
      return true
    }
  }

  /** The sheet's rows or columns changed: read it again; my edits go again
      once the change that moved them is here. */
  private async stale(sheetId: Id): Promise<void> {
    for (let i = 0; i < STALE_TRIES && !this.disposed; i++) {
      await this.reloadSheet(sheetId)
      const sheet = this.model.sheets.get(sheetId)
      if (!sheet) return
      const ops = this.model.pendingOf(sheetId)
      if (ops.every((o) => o.sv === sheet.structure_version)) {
        this.model.requeue()
        return
      }
      await sleep(STALE_WAIT_MS * (i + 1))
    }
    const n = this.model.dropOps(sheetId)
    if (n) this.deps.toast(STALE_DROPPED(n))
    this.scheduleFlush()
  }

  // ---- sheets and names (sent at once) ----

  async addSheet(name: string): Promise<TensorSheet> {
    const r = await this.deps.api.addSheet(this.workbookId, name)
    this.model.putSheet(r.sheet)
    this.model.loadedSv.set(r.sheet.id, r.sheet.structure_version)
    this.scheduleFlush()
    this.changed()
    return r.sheet
  }

  async updateSheet(id: Id, body: TensorSheetInput): Promise<TensorSheet> {
    const r = await this.deps.api.updateSheet(id, body)
    this.model.putSheet(r.sheet)
    this.scheduleFlush()
    this.changed()
    return r.sheet
  }

  async deleteSheet(id: Id): Promise<void> {
    await this.deps.api.deleteSheet(id)
    this.model.removeSheet(id)
    this.scheduleFlush()
    this.changed()
  }

  async addName(name: string, formula: string): Promise<TensorName> {
    const r = await this.deps.api.addName(this.workbookId, name, formula)
    this.model.putName(r.name)
    this.scheduleFlush()
    this.changed()
    return r.name
  }

  async updateName(id: Id, body: { name?: string; formula?: string }): Promise<void> {
    const r = await this.deps.api.updateName(id, body)
    this.model.putName(r.name)
    this.scheduleFlush()
    this.changed()
  }

  async deleteName(id: Id): Promise<void> {
    await this.deps.api.deleteName(id)
    this.model.names.delete(id)
    this.scheduleFlush()
    this.changed()
  }

  /** Cells written in one go (an import), PUT by PUT; the answers are the cells. */
  async uploadCells(sheetId: Id, cells: TensorCellInput[], progress?: (done: number) => void): Promise<void> {
    let done = 0
    for (let i = 0; i < cells.length; i += CELLS_PER_PUT) {
      const sheet = this.model.sheets.get(sheetId)
      if (!sheet) throw new Error('sheet gone')
      const part = cells.slice(i, i + CELLS_PER_PUT)
      const r = await this.deps.api.setCells(sheetId, sheet.structure_version, part)
      this.answerCells(sheetId, r.structure_version, r.cells)
      done += part.length
      progress?.(done)
      this.scheduleFlush()
    }
    this.changed()
  }
}

/** "2026-10" of an Excel serial. */
function monthOfSerial(serial: number): string {
  const d = new Date((serial - 25569) * 86400000)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}
